// frontend/src/pages/Orders.jsx - COMPLETELY CLEANED
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getUserOrders } from "../api";
import { useAuth } from "../context/AuthContext";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faBox,
  faTruck,
  faClock,
  faCheckCircle,
  faTimes,
  faEye,
  faSearch,
  faFilter,
  faSeedling,
  faPhone,
  faEnvelope,
  faCalendarAlt,
  faUser,
  faGlobe,
  faShip,
  faBuilding,
  faArrowRight,
  faSpinner,
  faExclamationTriangle,
  faCheckDouble,
  faShippingFast,
  faBoxOpen,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import SEO from "../components/SEO/SEO";

// ============================================
// CONSTANTS
// ============================================
const STATUS_OPTIONS = [
  { value: "all", label: "All Orders" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "confirmed", label: "Confirmed" },
  { value: "shipped", label: "Shipped" },
  { value: "out_for_delivery", label: "Out for Delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faClock,
    processing: faSpinner,
    confirmed: faCheckCircle,
    shipped: faShippingFast,
    out_for_delivery: faTruck,
    delivered: faCheckDouble,
    cancelled: faTimes,
    refunded: faTimes,
  };
  return icons[status] || faClock;
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    processing: "Processing",
    confirmed: "Confirmed",
    shipped: "Shipped",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
    refunded: "Refunded",
  };
  return labels[status] || status;
};

// ============================================
// MAIN COMPONENT
// ============================================
const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await getUserOrders();
        setOrders(data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  if (loading) return <Loader />;

  const isFarmer = user?.userType === "farmer";
  const bulkOrders = orders.filter(
    (o) => o.orderType === "bulk_farmer" || o.totalQuantity >= 100,
  );

  const filteredOrders = orders
    .filter((o) => filter === "all" || o.status === filter)
    .filter((o) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        o.orderNumber?.toLowerCase().includes(search) ||
        o.userName?.toLowerCase().includes(search) ||
        o.customerName?.toLowerCase().includes(search) ||
        o.items?.some((item) => item.name?.toLowerCase().includes(search))
      );
    });

  return (
    <>
      <SEO
        title="My Orders - Kenali Enterprise"
        description="Track and manage your orders from Kenali Enterprise. View order status, tracking information, and delivery updates."
        keywords="orders, Kenali Enterprise, order tracking, livestock feed"
        canonical="https://kenalienterprise.com/orders"
      />
      <motion.div
        className="orders-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Hero */}
        <section className="page-hero">
          <div className="page-hero-overlay" />
          <div className="container page-hero-content">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FontAwesomeIcon icon={faBox} className="page-hero-icon" />
              <h1>My Orders</h1>
              <p>
                Track and manage all your orders from Kenali Enterprise. All
                products are sourced from trusted global partners.
              </p>
              <div className="page-hero-badges">
                <span>
                  <FontAwesomeIcon icon={faGlobe} /> Global Sourcing
                </span>
                <span>
                  <FontAwesomeIcon icon={faShip} /> Premium Quality
                </span>
              </div>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="page-hero-whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> Need help? WhatsApp us
              </a>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <div className="container orders-container">
          {/* Farmer Notice */}
          {isFarmer && bulkOrders.length > 0 && (
            <motion.div
              className="farmer-notice"
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <FontAwesomeIcon icon={faBox} />
              <div>
                <strong>Farmer Account</strong>
                <span>
                  Bulk orders receive priority shipping and dedicated support.
                </span>
              </div>
              <span className="farmer-notice-count">
                {bulkOrders.length} Bulk Order
                {bulkOrders.length !== 1 ? "s" : ""}
              </span>
            </motion.div>
          )}

          {/* Empty State */}
          {orders.length === 0 ? (
            <motion.div
              className="orders-empty"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div className="orders-empty-icon">📦</div>
              <h3>No Orders Yet</h3>
              <p>You haven't placed any orders yet.</p>
              <div className="orders-empty-actions">
                <Link to="/products" className="shop-link">
                  <FontAwesomeIcon icon={faSeedling} /> Start Shopping
                </Link>
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-link"
                >
                  <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Us
                </a>
              </div>
            </motion.div>
          ) : (
            <>
              {/* Filters */}
              <div className="orders-filters">
                <div className="filters-scroll">
                  <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                  {STATUS_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFilter(option.value)}
                      className={`filter-btn ${filter === option.value ? "active" : ""}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Results Count */}
              <div className="orders-count">
                <span>
                  Showing {filteredOrders.length} order
                  {filteredOrders.length !== 1 ? "s" : ""}
                </span>
                {filter !== "all" && (
                  <button
                    onClick={() => setFilter("all")}
                    className="clear-filter"
                  >
                    <FontAwesomeIcon icon={faTimes} /> Clear Filter
                  </button>
                )}
              </div>

              {/* Orders List */}
              <div className="orders-list">
                {filteredOrders.length === 0 ? (
                  <div className="orders-no-results">
                    No orders found matching your criteria.
                  </div>
                ) : (
                  <AnimatePresence>
                    {filteredOrders.map((order, index) => {
                      const isBulk =
                        order.orderType === "bulk_farmer" ||
                        order.totalQuantity >= 100;
                      return (
                        <motion.div
                          key={order._id}
                          className={`order-card ${isBulk ? "bulk" : ""}`}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ y: -2 }}
                        >
                          <Link
                            to={`/orders/${order._id}`}
                            className="order-card-link"
                          >
                            <div className="order-card-left">
                              <div
                                className={`order-icon ${isBulk ? "bulk" : "regular"}`}
                              >
                                <FontAwesomeIcon
                                  icon={isBulk ? faTruck : faBox}
                                />
                              </div>
                              <div>
                                <div className="order-number">
                                  Order #{order.orderNumber}
                                  {isBulk && (
                                    <span className="bulk-badge">BULK</span>
                                  )}
                                  {order.isPreOrder && (
                                    <span className="preorder-badge">
                                      <FontAwesomeIcon icon={faClock} />{" "}
                                      Pre-Order
                                    </span>
                                  )}
                                </div>
                                <div className="order-meta">
                                  <span>
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                    {new Date(
                                      order.createdAt,
                                    ).toLocaleDateString()}
                                  </span>
                                  <span>
                                    <FontAwesomeIcon icon={faBox} />
                                    {order.items?.length || 0} items
                                  </span>
                                  <span>
                                    <FontAwesomeIcon icon={faWarehouse} />
                                    {order.totalQuantity || 0}kg
                                  </span>
                                  <span>
                                    <FontAwesomeIcon icon={faGlobe} /> Premium
                                    Quality
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="order-card-right">
                              <div className="order-total">
                                <div className="total-label">Total</div>
                                <div className="total-amount">
                                  <CurrencyDisplay
                                    amountUSD={
                                      order.total?.usd ||
                                      order.totalAmount?.usd ||
                                      0
                                    }
                                    compact
                                  />
                                </div>
                              </div>

                              <div
                                className="order-status"
                                style={{
                                  background: getStatusColor(order.status),
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={getStatusIcon(order.status)}
                                />
                                {getStatusLabel(order.status)}
                              </div>

                              <div className="order-view">
                                View Details <FontAwesomeIcon icon={faEye} />
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                )}
              </div>

              {/* Support Section */}
              <div className="orders-support">
                <FontAwesomeIcon icon={faWhatsapp} />
                <div>
                  <strong>Need help with your order?</strong>
                  <span>
                    Contact our support team for assistance with tracking,
                    returns, or any questions.
                  </span>
                </div>
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="support-link"
                >
                  <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Support
                </a>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default Orders;
